import React from 'react'
import { Element, Text } from 'domhandler/lib/node'
import { gsap } from 'gsap'
import { GatsbyImage } from 'gatsby-plugin-image'
import parse, { attributesToProps, domToReact, HTMLReactParserOptions } from 'html-react-parser'
import { FileEdge, Query } from '~/types/graphql-types'
import Blogcard from '~/components/Common/Blogcard'

/**
 * cssのfont-sizeをrem変換
 * @param {number} fontSize - px数
 * @return {string} remの値
 */
export function rem(fontSize: number): string {
  return `${fontSize / 16}rem`
}

/**
 * スクロールアニメーションの設定
 * @return void
 */
export function setScrollAnimation() {
  const targets = gsap.utils.toArray<HTMLElement>('[data-show-scroll="true"]')
  targets.forEach((target) => {
    gsap.fromTo(
      target,
      {
        autoAlpha: 0,
        y: -16,
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.36,
        delay: 0,
        scrollTrigger: {
          trigger: target,
          start: 'top 80%',
        },
      },
    )
  })
}

/**
 * htmlタグを含んだ文字列を、除外した文字列に変換
 * @param {string} html - htmlタグを含んだ文字列
 * @return {string} htmlタグを除外した文字列
 */
export function convertHtmlToText(html: string) {
  return html.replace(/<("[^"]*"|'[^']*'|[^'">])*>/g, '')
}

/**
 * microCMSの画像をtsxで使うための関数
 * @param {Query['allFile']} allFile - GraphQLのallFile
 * @param {string} url- 画像のURL
 * @return {gatsbyImageData}
 */
export function getGatsbyImageData(allFile: Query['allFile'], url: string) {
  const image = allFile.edges.find((edge: FileEdge) => {
    return edge.node.url === url
  })
  if (
    !image ||
    !image.node.childrenImageSharp ||
    !Array.isArray(image.node.childrenImageSharp) ||
    !image.node.childrenImageSharp[0]
  )
    return
  return image.node.childrenImageSharp[0].gatsbyImageData
}

/**
 * microCMSのリッチテキストに挿入された画像をGatsbyImageに置き換える
 * @param {string} content - htmlタグを含んだ文字列
 * @param {Query['allFile']} allFile - htmlタグを含んだ文字列
 * @return {ReactDOM} 画像を置き換えたDOM
 */
export function replaceImage(content: string, allFile: Query['allFile']) {
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      const element = domNode as Element
      switch (element.name) {
        case 'p': {
          const props = attributesToProps(element.attribs)
          return <div {...props}>{domToReact(element.children, parserOptions)}</div>
        }
        case 'img': {
          const gatsbyImageData = getGatsbyImageData(allFile, element.attribs.src)
          return <GatsbyImage className="image" image={gatsbyImageData} alt="" />
        }
      }
    },
  }
  return parse(content, parserOptions)
}

/**
 * microCMSのリッチテキストに挿入されたURLのみのaタグをBlogCardに置き換える
 * @param {string} content - htmlタグを含んだ文字列
 * @param {Query['allOgp']} allOgp - htmlタグを含んだ文字列
 * @return {ReactDOM} aタグを置き換えたDOM
 */
export function replaceBlogcard(content: string, allOgp: Query['allOgp']) {
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      const element = domNode as Element
      switch (element.name) {
        case 'a': {
          const href = element.attribs.href
          if (href !== (element.childNodes[0] as Text).data) return

          const ogp = allOgp.nodes.find((node) => {
            return node.url === href
          })
          if (!ogp) return
          return <Blogcard ogp={ogp} element={element} />
        }
      }
    },
  }
  return parse(content, parserOptions)
}

/**
 * microCMSのリッチテキストに挿入された画像をGatsbyImageに置き換える ＆ URLのみのaタグをBlogCardに置き換える
 * @param {string} content - htmlタグを含んだ文字列
 * @param {Query['allFile']} allFile - htmlタグを含んだ文字列
 * @param {Query['allOgp']} allOgp - htmlタグを含んだ文字列
 * @return {ReactDOM} 画像とaタグを置き換えたDOM
 */

export function replaceImageAndBlogcard(content: string, allFile: Query['allFile'], allOgp: Query['allOgp']) {
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      const element = domNode as Element
      switch (element.name) {
        case 'p': {
          const props = attributesToProps(element.attribs)
          return <div {...props}>{domToReact(element.children, parserOptions)}</div>
        }
        case 'img': {
          const gatsbyImageData = getGatsbyImageData(allFile, element.attribs.src)
          return <GatsbyImage className="image" image={gatsbyImageData} alt="" />
        }
        case 'a': {
          const href = element.attribs.href
          if (href !== (element.childNodes[0] as Text).data) return

          const ogp = allOgp.nodes.find((node) => {
            return node.url === href
          })
          if (!ogp) return
          return <Blogcard ogp={ogp} element={element} />
        }
      }
    },
  }
  return parse(content, parserOptions)
}
